/**
 * Tons da paleta 2027. Vários tokens legados (Blue100…Blue700, A350, BlueChart,
 * BlueMenu, DarkBlue) apontam para o mesmo tom: são aliases mantidos por
 * compatibilidade, não degraus distintos de uma escala. Em código novo, prefira
 * os tokens por papel (`brand`, `Accent`, `menu`, `text`, `bg`...).
 */
const palette2027 = {
  navy: '#0A0E27',
  /** Cor principal do manual 2027 (fundo do menu lateral). */
  principal: '#0B0C59',
  primaryDark: '#2A2F86',
  lilac: '#8DA2FF',
  accent: '#00B9CE',
};

export const colors = {
  Black: '#000',
  White: '#fff',
  A350: palette2027.primaryDark,
  Grey030: '#F8F8F8',
  Grey050: '#E9E7E9',
  Grey100: '#74747433',
  Grey150: '#CDD0D5',
  Grey200: '#ABB0B4',
  Grey300: '#77818B',
  Grey400: '#464555',
  Grey500: '#5B5B5B',
  GreyInputBorder: '#818181',
  GreyInputPlaceholder: '#666666',
  GreyDefaultCardBorder: '#D7D7D7',
  GreyBorderSoft: '#DDDDDD',
  Pink100: '#FF5473',
  Surface700: '#20232D',

  Pink200: '#ED193F',

  Pink300: '#D60E32',
  Pink400: '#B90B2E',
  Blue050: '#C8D8FD',
  Blue100: palette2027.lilac,
  /** Lilás: só sobre fundo escuro (2,4:1 sobre branco). Para texto em fundo claro, use `brand.primary.dark`. */
  Blue200: palette2027.lilac,
  Blue300: '#363BC4',
  Blue400: palette2027.primaryDark,
  Blue500: palette2027.navy,
  Blue600: palette2027.navy,
  Blue700: palette2027.primaryDark,
  BlueSecondary: '#363BC4',
  BlueChart: palette2027.primaryDark,

  /** Acento de marca 2027 (ciano). Texto sobre ele: `Blue500`. */
  Accent: palette2027.accent,
  /** @deprecated use `Accent` (guarda o ciano de marca, não um verde). */
  GrenTab: palette2027.accent,
  BlueMenu: palette2027.navy,
  Offwhite: '#DEDEDE',
  Red: '#DC0E01',
  Orange: '#FF5C00',
  LightOrange: '#FFBE16',
  Yellow: '#FDB400',
  Green: '#58CC00',
  LightBlue: '#4C87C7',
  MediumBlue: '#2A67A9',
  DarkBlue: palette2027.navy,
  DarkGrey: '#5D5D5D',
  VeryDarkGrey: '#404040',
  DarkGrey70: 'rgba(93, 93, 93, 0.7)',
  Grey: '#DBDBDB',
  GreyDark: '#3C3C3C',
  GreyLight: '#E9E9E9',
  MediumGrey: '#bfbfbf',
  LightGrey: '#F5F5F5',
  Red_v2: '#D94438', // f44336
  LightRed: '#eee0de',
  VeryLightBlue: '#e0e5ea',
  Green_v2: '#299656',
  Yellow_v2: '#EACB3E',
  Orange600: '#be5f0f',
  Orange_v2: '#E5954D',
  RedDark: '#E00030',
  GreenLight: '#5AB365',
  Yellow_v3: '#F8D000',
  Orange_v3: '#FF4D00',
  Grey_v3: '#BBBBBB',
  LightGrey_v3: '#C4C4C4',
  LightLightGrey_v3: '#ECECEC',
  BlueSecondary_v3: '#2D81FF',
  BlueSelectedSpan: '#BEC0FF',
  text: {
    'sub-300': '#CDD0D5',
    'sub-350': '#86909C',
    'sub-500': '#525866',
    'soft-400': '#868C98',
    'main-650': '#2E2E30',
    'main-900': '#0A0D14',
    'primary-base': '#363BC4',
    'white-0': '#FFFFFF',
    'danger-base': '#DD1818',
    'disabled-300': '#CDD0D5',
    'primary-dark': palette2027.primaryDark,
  },
  brand: {
    /** Cor principal do manual 2027: menu lateral, botão primário e cabeçalhos de tabela e de Card. */
    principal: palette2027.principal,
    /** Hover dos botões: o ciano do menu lateral, com o texto em branco (escolha de marca; fica abaixo do AA de contraste). */
    principalHover: palette2027.accent,
    primary: {
      base: '#363BC4',
      dark: palette2027.primaryDark,
      light: '#C8D8FD',
      lighter: '#EEF4FF',
      lighterAlt: '#E0EAFF',
    },
  },
  bg: {
    'danger-light-0': '#FFDADA',
    'danger-light': '#FFA0A0',
    'primary-base': '#363BC4',
    'soft-200': '#E2E4E9',
    'surface-700': '#20232D',
    'weak-100': '#F6F8FA',
    'white-0': '#FFFFFF',
    'warn-light': '#FFEEBA',
    'success-light': '#C7F9B7',
    'soft-400': '#868C98',
  },
  icon: {
    'primary-base': '#363BC4',
    'primary-dark': palette2027.primaryDark,
    'danger-base': '#DD1818',
    'disabled-300': '#CDD0D5',
    'soft-400': '#868C98',
    'sub-500': '#525866',
    'strong-800': '#161922',
    'strong-900': '#0A0D14',
    'white-0': '#FFFFFF',
    'warn-base': '#F28602',
  },
  /** Menu lateral (desktop e mobile). */
  menu: {
    background: palette2027.principal,
    activeItem: 'rgba(0, 185, 206, 0.22)',
    hoverItem: 'rgba(255, 255, 255, 0.08)',
    subItemBackground: 'rgba(0, 0, 0, 0.22)',
    activeSubItem: 'rgba(0, 185, 206, 0.18)',
    hoverSubItem: 'rgba(255, 255, 255, 0.06)',
    sectionTitle: palette2027.lilac,
  },
  neutral: {
    900: '#0A0D14',
  },
  stroke: {
    'primary-base': '#363BC4',
    'danger-base': '#DD1818',
    'soft-200': '#E2E4E9',
    'weak-100': '#F6F8FA',
    'surface-700': '#20232D',
    'sub-300': '#CDD0D5',
  },
  state: {
    danger: {
      base: '#DD1818',
      dark: '#C21313',
    },
    info: {
      base: '#00B9CE',
      dark: '#0A7C94',
    },
    success: {
      base: '#39B719',
      dark: '#206014',
    },
    warning: {
      base: '#F28602',
      dark: '#A0500C',
    },
    cold: {
      base: '#3180C6',
      dark: '#1B4571',
      lighter: '#F2F7FD',
    },
  },
};
